<template>
    <!-- The stage drawn big as a winding road: start, stops and finish, an
         activity tick per activity, the stretch already ridden filled in and
         the theme's rider on it. Markers/labels are HTML laid over the SVG
         (same aspect ratio, so percentages line up) to keep text crisp. -->
    <div ref="root" class="stage-route" :class="'stage-route-' + mode">
        <svg class="stage-route-svg" :viewBox="'0 0 ' + W + ' ' + H" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
            <path ref="road" class="stage-route-road" :d="ROAD"
                :style="mode == 'draw' ? { strokeDasharray: length, strokeDashoffset: length, animationDuration: draw_ms + 'ms', animationDelay: draw_delay_ms + 'ms' } : null" />
            <path class="stage-route-ridden" :d="ROAD"
                :style="{ strokeDasharray: length, strokeDashoffset: length * (1 - fill) }" />
            <template v-for="tick in ticks" :key="'t' + tick.i">
                <!-- Content activities: diamond, same convention as StageProgress. -->
                <g v-if="utils.is_content_activity(tick.type)" :transform="'translate(' + tick.x + ' ' + tick.y + ') rotate(45)'">
                    <rect class="stage-route-tick" :class="{ 'stage-route-tick-done': tick.f <= fill + 0.0001 }"
                        x="-5" y="-5" width="10" height="10" :style="pop_style(tick.f)" />
                </g>
                <circle v-else class="stage-route-tick"
                    :class="{ 'stage-route-tick-done': tick.f <= fill + 0.0001 }"
                    :cx="tick.x" :cy="tick.y" r="5" :style="pop_style(tick.f)" />
            </template>
        </svg>

        <div class="stage-route-marker stage-route-marker-end" :class="{ 'stage-route-marker-near': rider_near(0) }" :style="marker_style(0)">
            <i :class="'bi ' + theme.icons.start"></i>
        </div>
        <div v-for="stop in placed_stops" :key="'s' + stop.id"
            class="stage-route-marker stage-route-marker-stop"
            :class="{ 'stage-route-marker-current': current_stop && stop.id == current_stop.id, 'stage-route-marker-passed': stop.f < fill - 0.0001 }"
            :style="marker_style(stop.f)">
            <i :class="'bi ' + theme.icons.stop"></i>
        </div>
        <div class="stage-route-marker stage-route-marker-end" :class="{ 'stage-route-marker-near': rider_near(1) }" :style="marker_style(1)">
            <i :class="'bi ' + theme.icons.finish"></i>
        </div>

        <!-- Labels live apart from their markers: layout_labels() places
             each one above/below its marker, pushing it out to a further
             lane when it would overlap another label or marker, and a
             vertical leader line ties it back to its marker. The further
             a label is from the rider, the fainter it gets. -->
        <div class="stage-route-labels" :class="{ 'stage-route-labels-ready': labels_ready }">
            <div v-for="label in labels" :key="'l' + label.key" class="stage-route-label-wrap" :style="pop_style(label.f)">
                <div class="stage-route-label-inner" :class="{ 'stage-route-label-current': label.current }" :style="{ opacity: emphasis(label) }">
                    <div v-if="label_pos[label.key]" class="stage-route-leader" :style="label_pos[label.key].line"></div>
                    <span :ref="'label-' + label.key" class="stage-route-label" :style="label_pos[label.key] ? label_pos[label.key].box : null">{{ label.text }}</span>
                </div>
            </div>
        </div>

        <div class="stage-route-rider" :class="{ 'stage-route-rider-in': rider_visible }" :style="rider_style">
            <component :is="theme.rider" :color="color" :size="rider_size" :moving="riding" />
        </div>
    </div>
</template>

<script>
const W = 1000;
const H = 320;
// Hand-drawn winding road, left to right. Any path works: positions are
// measured along it with getPointAtLength.
const ROAD = 'M 60 236 C 210 236, 250 120, 400 132 S 610 262, 760 186 S 900 96, 940 112';
// Fraction of the road kept between a marker and whatever would sit on it.
const MARKER_GAP = 0.06;
// Start/finish fade while the rider is closer than this (route fraction).
const NEAR_GAP = 0.1;
// Same idea between two markers (a stop sitting on the start/finish).
const STOP_GAP = 0.08;
// Further-out lanes a label may be pushed to (each side) to dodge others.
const LABEL_LANES = 4;
// Faintest a far-away label gets, and how fast it fades with distance.
const LABEL_MIN_OPACITY = 0.35;
const LABEL_FADE = 1.6;

// Purely visual: what to draw comes in `route` (server's _stage_route: total
// activities, stops by position, `done`), when to move is driven by the
// timeline props so Interlude.vue owns the choreography.
export default {
    name: 'StageRoute',
    props: {
        route: { type: Object, required: true },
        theme: { type: Object, required: true },
        color: { type: String, default: '#00a7af' },
        // 'draw': the road draws itself left to right and markers pop in as
        // it reaches them (intro). 'ride': everything is there, the rider
        // rides from ride_from to ride_to (checkpoint).
        mode: { type: String, default: 'draw' },
        draw_delay_ms: { type: Number, default: 0 },
        draw_ms: { type: Number, default: 3000 },
        // Fractions of the route (0 = start, 1 = finish).
        ride_from: { type: Number, default: 0 },
        ride_to: { type: Number, default: 0 },
        ride_delay_ms: { type: Number, default: 0 },
        ride_ms: { type: Number, default: 2400 },
        rider_delay_ms: { type: Number, default: 0 },
    },
    data(){
        return {
            utils: require('../../../shared/utils'),
            W, H, ROAD,
            length: 1,
            points: null,
            rider_f: this.ride_from,
            riding: false,
            rider_visible: false,
            rider_size: 52,
            label_pos: {},
            labels_ready: false,
        };
    },
    computed: {
        total(){
            return Math.max(1, this.route.total || 0);
        },
        // Activity i covers the stretch i..i+1, its tick sits mid-way.
        ticks(){
            if(!this.points) return [];
            return (this.route.types || []).map((type, i) => {
                const f = (i + 0.5) / this.total;
                const p = this.point(f);
                return { i, f, type, x: p.x, y: p.y };
            });
        },
        // A stop right at the start/finish (e.g. a podium after the last
        // activity) is nudged off it so the two markers don't stack.
        placed_stops(){
            return (this.route.stops || []).map((stop) => {
                const f = Math.min(1, stop.position / this.total);
                return Object.assign({}, stop, { f: Math.max(STOP_GAP, Math.min(1 - STOP_GAP, f)) });
            });
        },
        // The stop we're arriving at (checkpoint): the one at `done`.
        current_stop(){
            if(this.mode != 'ride') return null;
            return this.placed_stops.find((s) => s.position == this.route.done) || null;
        },
        // Every marker's name: start, stops, finish.
        labels(){
            return [
                { key: 'start', f: 0, text: this.theme.texts.start },
                ...this.placed_stops.map((stop) => ({
                    key: 's' + stop.id, f: stop.f, text: stop.name,
                    current: !!(this.current_stop && stop.id == this.current_stop.id),
                })),
                { key: 'finish', f: 1, text: this.theme.texts.finish },
            ];
        },
        fill(){
            return this.mode == 'ride' ? this.rider_f : 0;
        },
        // The rider stops just short of the marker it's heading to (and
        // just past the start) instead of standing on top of it.
        rider_at(){
            return Math.max(MARKER_GAP, this.rider_f - (this.rider_f > MARKER_GAP ? MARKER_GAP : 0));
        },
        rider_style(){
            const p = this.point(this.rider_at);
            return {
                left: (p.x / W * 100) + '%',
                top: (p.y / H * 100) + '%',
                transform: 'translate(-50%, -100%) rotate(' + p.angle + 'deg)',
            };
        }
    },
    mounted(){
        const road = this.$refs.road;
        this.length = road.getTotalLength();
        // Sample once - cheap lookups for every frame afterwards.
        const samples = 400;
        this.points = Array.from({ length: samples + 1 }, (_, i) => {
            const pt = road.getPointAtLength(this.length * i / samples);
            return { x: pt.x, y: pt.y };
        });
        this.resizeObserver = new ResizeObserver(entries => {
            this.rider_size = Math.max(36, Math.min(84, entries[0].contentRect.width * 0.09));
            cancelAnimationFrame(this.layout_frame);
            this.layout_frame = requestAnimationFrame(this.layout_labels);
        });
        this.resizeObserver.observe(this.$refs.root);
        // Label widths depend on the web font - lay out again once it's in.
        this.$nextTick(this.layout_labels);
        if(document.fonts && document.fonts.ready)
            document.fonts.ready.then(() => { if(this.$refs.root) this.layout_labels(); });

        this.timers = [];
        this.frame = null;
        this.timers.push(setTimeout(() => { this.rider_visible = true; }, this.rider_delay_ms));
        if(this.mode == 'ride' && this.ride_to != this.ride_from)
            this.timers.push(setTimeout(this.ride, this.ride_delay_ms));
    },
    beforeUnmount(){
        this.resizeObserver?.disconnect();
        cancelAnimationFrame(this.layout_frame);
        (this.timers || []).forEach(clearTimeout);
        if(this.frame) cancelAnimationFrame(this.frame);
    },
    watch: {
        labels(){ this.$nextTick(this.layout_labels); },
    },
    methods: {
        // Places every label (in px, relative to the root) so none overlaps
        // another label or a marker: tries right next to its marker first,
        // above or below, then further-out lanes. The current stop goes
        // first so it gets the best spot (below - the rider rides above).
        layout_labels(){
            const root = this.$refs.root;
            if(!root || !this.points) return;
            const width = root.clientWidth;
            const height = root.clientHeight;
            if(!width || !height) return;
            const sx = width / W;
            const sy = height / H;
            const radius = Math.max(24, Math.min(54, width * 0.05)) / 2;
            const gap = Math.max(4, width * 0.008);
            const pad = 3;

            const anchors = this.labels.map((label) => {
                const p = this.point(label.f);
                const el = [].concat(this.$refs['label-' + label.key] || [])[0];
                return { label, x: p.x * sx, y: p.y * sy, w: el ? el.offsetWidth : 0, h: el ? el.offsetHeight : 0 };
            });
            // Markers are obstacles too.
            const placed = anchors.map((a) => ({ l: a.x - radius, r: a.x + radius, t: a.y - radius, b: a.y + radius }));
            const overlaps = (box) => placed.some((o) => box.l < o.r + pad && box.r > o.l - pad && box.t < o.b + pad && box.b > o.t - pad);

            const order = anchors.map((a, i) => i).sort((i, j) =>
                (anchors[j].label.current ? 1 : 0) - (anchors[i].label.current ? 1 : 0) || anchors[i].label.f - anchors[j].label.f);

            const result = {};
            order.forEach((idx, n) => {
                const a = anchors[idx];
                if(!a.w) return;
                const lane_h = a.h + gap;
                // Alternate the preferred side so neighbours start apart.
                const prefer_below = a.label.current || n % 2 == 0;
                const sides = prefer_below ? ['below', 'above'] : ['above', 'below'];
                const left = Math.max(0, Math.min(width - a.w, a.x - a.w / 2));
                const box_at = (side, lane) => {
                    const top = side == 'below'
                        ? a.y + radius + gap + lane * lane_h
                        : a.y - radius - gap - lane * lane_h - a.h;
                    return { side, l: left, r: left + a.w, t: top, b: top + a.h };
                };
                let box = null;
                for(let lane = 0; lane < LABEL_LANES && !box; lane++)
                    box = sides.map((side) => box_at(side, lane)).find((b) => !overlaps(b)) || null;
                if(!box) box = box_at(sides[0], 0);
                placed.push(box);

                const line_top = box.side == 'below' ? a.y + radius : box.b;
                const line_bottom = box.side == 'below' ? box.t : a.y - radius;
                result[a.label.key] = {
                    box: { left: box.l + 'px', top: box.t + 'px' },
                    line: { left: a.x + 'px', top: line_top + 'px', height: Math.max(0, line_bottom - line_top) + 'px' },
                };
            });
            this.label_pos = result;
            this.labels_ready = true;
        },
        // Closer to where the rider is = more opaque.
        emphasis(label){
            if(label.current) return 1;
            const distance = Math.abs(label.f - this.rider_at);
            return Math.max(LABEL_MIN_OPACITY, 1 - distance * LABEL_FADE).toFixed(2);
        },
        point(f){
            if(!this.points) return { x: 0, y: 0, angle: 0 };
            const n = this.points.length - 1;
            const i = Math.max(0, Math.min(n, Math.round(f * n)));
            const a = this.points[Math.max(0, i - 4)];
            const b = this.points[Math.min(n, i + 4)];
            // Tilt with the road, a bit damped so it doesn't look drunk.
            const angle = Math.atan2(b.y - a.y, b.x - a.x) * 180 / Math.PI * 0.6;
            return { x: this.points[i].x, y: this.points[i].y, angle };
        },
        ride(){
            const start = performance.now();
            const from = this.ride_from;
            // Ride to where the stop is actually drawn (it may be nudged off
            // the finish, see placed_stops).
            const to = this.current_stop ? this.current_stop.f : this.ride_to;
            this.riding = true;
            const step = (now) => {
                const t = Math.min(1, (now - start) / this.ride_ms);
                const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
                this.rider_f = from + (to - from) * eased;
                if(t < 1){
                    this.frame = requestAnimationFrame(step);
                }else{
                    this.frame = null;
                    this.riding = false;
                    this.$emit('arrived');
                }
            };
            this.frame = requestAnimationFrame(step);
        },
        rider_near(f){
            return this.rider_visible && Math.abs(this.rider_at - f) < NEAR_GAP;
        },
        // Intro: each marker pops in when the drawing road reaches it.
        pop_style(f){
            if(this.mode != 'draw') return null;
            return { animationDelay: (this.draw_delay_ms + f * this.draw_ms) + 'ms' };
        },
        marker_style(f){
            const p = this.point(f);
            return Object.assign({ left: (p.x / W * 100) + '%', top: (p.y / H * 100) + '%' }, this.pop_style(f) || {});
        }
    },
    emits: ['arrived'],
}
</script>

<style scoped>
    /* Sizes below are in cqw (% of this route's own width), so the same
       drawing works on a phone and on the projected big screen. */
    .stage-route{
        position: relative;
        width: 100%;
        aspect-ratio: 1000 / 320;
        container-type: inline-size;
    }
    .stage-route-svg{
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        overflow: visible;
    }
    .stage-route-road,
    .stage-route-ridden{
        fill: none;
        stroke-width: 16;
        stroke-linecap: round;
        stroke-linejoin: round;
    }
    .stage-route-road{
        stroke: rgba(255, 255, 255, 0.18);
    }
    .stage-route-draw .stage-route-road{
        animation-name: route-draw;
        animation-timing-function: ease-in-out;
        animation-fill-mode: forwards;
    }
    @keyframes route-draw{ to{ stroke-dashoffset: 0; } }
    .stage-route-ridden{
        stroke: var(--vc-accent-500, #00a7af);
    }

    .stage-route-tick{
        fill: var(--vc-ink-900, #17202b);
        stroke: rgba(255, 255, 255, 0.55);
        stroke-width: 3;
        transform-box: fill-box;
        transform-origin: center;
    }
    .stage-route-tick-done{
        fill: var(--vc-accent-500, #00a7af);
        stroke: var(--vc-accent-500, #00a7af);
    }

    .stage-route-marker{
        position: absolute;
        transform: translate(-50%, -50%);
        width: clamp(24px, 5cqw, 54px);
        height: clamp(24px, 5cqw, 54px);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: clamp(0.75rem, 2.2cqw, 1.5rem);
        background-color: var(--vc-ink-900, #17202b);
        border: clamp(2px, 0.3cqw, 3px) solid #fff;
        color: #fff;
        z-index: 2;
    }
    .stage-route-marker-end{
        background-color: #fff;
        color: var(--vc-ink-900, #17202b);
    }
    /* !important so it also wins over the intro pop animation's fill. */
    .stage-route-marker-near{
        opacity: 0.4 !important;
    }
    .stage-route-marker-stop{
        border-color: var(--vc-accent-500, #00a7af);
        color: var(--vc-accent-500, #00a7af);
    }
    .stage-route-marker-passed{
        background-color: var(--vc-accent-500, #00a7af);
        color: #fff;
    }
    .stage-route-marker-current{
        animation: stop-pulse 1.2s ease-in-out infinite;
    }
    @keyframes stop-pulse{
        0%, 100%{ box-shadow: 0 0 0 0 rgba(0, 167, 175, 0.7); }
        50%{ box-shadow: 0 0 0 16px rgba(0, 167, 175, 0); }
    }

    /* Labels layer: positions come from layout_labels() (px). Hidden
       until the first layout so they never flash at 0,0. */
    .stage-route-labels{
        position: absolute;
        inset: 0;
        z-index: 2;
        pointer-events: none;
        visibility: hidden;
    }
    .stage-route-labels-ready{
        visibility: visible;
    }
    .stage-route-label-wrap,
    .stage-route-label-inner{
        position: absolute;
        inset: 0;
    }
    .stage-route-label-inner{
        transition: opacity 0.4s;
    }
    .stage-route-label{
        position: absolute;
        white-space: nowrap;
        font-family: "Archivo", sans-serif;
        font-weight: 800;
        font-size: clamp(0.6rem, 1.6cqw, 1.05rem);
        line-height: 1.2;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #fff;
        text-shadow: 0 2px 6px rgba(0, 0, 0, 0.6);
    }
    .stage-route-label-current .stage-route-label{
        color: var(--vc-accent-500, #00a7af);
    }
    .stage-route-leader{
        position: absolute;
        width: 1px;
        transform: translateX(-50%);
        background-color: rgba(255, 255, 255, 0.55);
    }

    /* Intro: markers and ticks pop in as the road reaches them. */
    .stage-route-draw .stage-route-marker,
    .stage-route-draw .stage-route-label-wrap,
    .stage-route-draw .stage-route-tick{
        opacity: 0;
        animation-name: marker-pop;
        animation-duration: 0.45s;
        animation-timing-function: cubic-bezier(0.34, 1.56, 0.64, 1);
        animation-fill-mode: forwards;
    }
    .stage-route-draw .stage-route-marker{
        /* keep the centring translate while popping */
        animation-name: marker-pop-centred;
    }
    /* The label layer spans the whole route - scaling it would grow from
       the route's centre, so labels just fade in. */
    .stage-route-draw .stage-route-label-wrap{
        animation-name: label-fade;
    }
    @keyframes label-fade{
        from{ opacity: 0; }
        to{ opacity: 1; }
    }
    @keyframes marker-pop{
        from{ opacity: 0; transform: scale(0.2); }
        to{ opacity: 1; transform: scale(1); }
    }
    @keyframes marker-pop-centred{
        from{ opacity: 0; transform: translate(-50%, -50%) scale(0.2); }
        to{ opacity: 1; transform: translate(-50%, -50%) scale(1); }
    }

    .stage-route-rider{
        position: absolute;
        z-index: 3;
        line-height: 0;
        transform-origin: 50% 100%;
        opacity: 0;
        transition: opacity 0.5s;
    }
    .stage-route-marker-end{
        transition: opacity 0.4s;
    }
    .stage-route-rider-in{
        opacity: 1;
    }

    @media (prefers-reduced-motion: reduce){
        .stage-route-draw .stage-route-road{ animation: none; stroke-dashoffset: 0 !important; }
        .stage-route-draw .stage-route-marker,
        .stage-route-draw .stage-route-label-wrap,
        .stage-route-draw .stage-route-tick{ animation: none; opacity: 1; }
        .stage-route-marker-current{ animation: none; }
    }
</style>
